import { useState, useCallback, useEffect, FC, useMemo } from 'react';
import { useCurrentCooperative } from 'hooks';

import {
  communicationMessagesApi,
  RecipientsBody,
} from 'services/communication-messages';

import {
  MassMessage as MassMessageModel,
  MassMessageBuilding,
  MassMessageRole,
  MassMessageRolesGroup,
  MassMessageStatusCode,
  BaseResponse,
  ExtendedMassMessage,
  MassMessageCooperative,
  MassMessageRolesGroupRes,
  MassMessageSpace,
  RoleEntityType,
} from 'models';

import axios, { AxiosResponse } from 'services';
import isRolesGroup from 'utils/isRolesGroup';
import orderBy from 'lodash/orderBy';

import { MassMessageDetails } from 'components';

function transformRolesGroups(
  rolesGroups: MassMessageRolesGroupRes[],
  isTalotiliCoop: boolean
): (MassMessageRole | MassMessageRolesGroup)[] {
  let entities: (MassMessageRole | MassMessageRolesGroup)[] = [];

  for (let rolesGroup of rolesGroups) {
    if (rolesGroup.UniqueName === 'owners') {
      entities.push({
        CanExpand: false,
        Order: 1,
        Name: rolesGroup.Name,
        Roles: rolesGroup.Items,
        Type: RoleEntityType.Group,
        UniqueName: rolesGroup.UniqueName,
      } as MassMessageRolesGroup);

      continue;
    }

    if (rolesGroup.UniqueName === 'residents') {
      entities.push({
        CanExpand: false,
        Name: rolesGroup.Name,
        Order: 2,
        Roles: rolesGroup.Items,
        Type: RoleEntityType.Group,
        UniqueName: rolesGroup.UniqueName,
      } as MassMessageRolesGroup);

      continue;
    }

    if (rolesGroup.UniqueName === 'board') {
      entities.push({
        CanExpand: true,
        Name: rolesGroup.Name,
        Order: 3,
        Roles: rolesGroup.Items,
        Type: RoleEntityType.Group,
        UniqueName: rolesGroup.UniqueName,
      } as MassMessageRolesGroup);

      continue;
    }

    if (rolesGroup.UniqueName === 'PMCroles') {
      rolesGroup.Items.forEach((role, idx) => {
        if (role.Name === 'Isännöitsijä') {
          if (isTalotiliCoop) return;

          entities.push({
            Type: RoleEntityType.Role,
            Name: role.Name,
            Order: 4 + idx,
            DefaultSelected: role.DefaultSelected,
            Id: role.Id,
            RelatedTo: role.RelatedTo,
          } as MassMessageRole);
        }

        if (role.Name === 'Kirjanpitäjä') {
          entities.push({
            Type: RoleEntityType.Role,
            Name: role.Name,
            Order: 4 + idx,
            DefaultSelected: role.DefaultSelected,
            Id: role.Id,
            RelatedTo: role.RelatedTo,
          } as MassMessageRole);
        }
      });

      continue;
    }
  }

  return orderBy(entities, 'Order', 'asc');
}

function getFilteredSpaces(buildings: MassMessageBuilding[], ids: string[]) {
  let spaces: MassMessageSpace[] = [];

  for (let building of buildings) {
    for (let space of building.Spaces) {
      if (ids.includes(space.Id)) {
        spaces.push(space);
      }
    }
  }

  return spaces;
}

function getFilteredRoles(
  rolesGroups: (MassMessageRole | MassMessageRolesGroup)[],
  ids: string[]
) {
  let roles: MassMessageRole[] = [];

  for (let roleEntity of rolesGroups) {
    if (isRolesGroup(roleEntity)) {
      for (let role of roleEntity.Roles) {
        if (ids.includes(role.Id)) {
          roles.push(role);
        }
      }
    } else {
      if (ids.includes(roleEntity.Id)) {
        roles.push(roleEntity);
      }
    }
  }

  return roles;
}

interface MassMessageProps {
  id: string | null;
  handleClose: (update?: boolean) => void;
}

interface InitialMassMessageData {
  communicationMessage: MassMessageModel | null;
  buildings: MassMessageBuilding[];
  rolesEntities: (MassMessageRole | MassMessageRolesGroup)[];
}

const MassMessage: FC<MassMessageProps> = ({ id: messageId, handleClose }) => {
  const cooperative = useCurrentCooperative();
  const coopId = cooperative?.Id ?? null;

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [initialMassMessageData, setInitialMassMessageData] =
    useState<InitialMassMessageData>({
      buildings: [],
      communicationMessage: null,
      rolesEntities: [],
    });

  const [isDistributed, setIsDistributed] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const { communicationMessage } = initialMassMessageData;

  const getInitialData = useCallback(
    async (coopId: string, messageId: string) => {
      try {
        setIsLoading(true);

        const [massMessagesRes, buildingsRes, rolesRes] =
          await Promise.allSettled([
            axios.get<
              unknown,
              AxiosResponse<BaseResponse<ExtendedMassMessage>>
            >(`/api/user/cooperatives/${coopId}/mass-messages/${messageId}`),
            axios.get<
              unknown,
              AxiosResponse<BaseResponse<MassMessageCooperative | null>>
            >(`/api/cooperatives/${coopId}/mass-messages/spaces`),
            axios.get<
              unknown,
              AxiosResponse<BaseResponse<MassMessageRolesGroupRes[]>>
            >(`/api/cooperatives/${coopId}/mass-messages/roles`),
          ]);

        if (massMessagesRes.status === 'rejected')
          throw new Error(massMessagesRes.reason);
        if (buildingsRes.status === 'rejected')
          throw new Error(buildingsRes.reason);
        if (rolesRes.status === 'rejected') throw new Error(rolesRes.reason);

        const massMessage = massMessagesRes.value.data.Data;
        const massMessageCooperative = buildingsRes.value.data.Data;
        const roles = rolesRes.value.data.Data;

        const buildings = massMessageCooperative?.Buildings ?? [];
        const rolesEntities = transformRolesGroups(
          roles,
          massMessage?.IsTalotiliCoop ?? false
        );

        setInitialMassMessageData({
          communicationMessage: {
            ...massMessage,
            Spaces: getFilteredSpaces(buildings, massMessage.Spaces),
            RecipientRoles: massMessage.RecipientRoles
              ? getFilteredRoles(rolesEntities, massMessage.RecipientRoles)
              : [],
          },
          buildings,
          rolesEntities,
        });
        setIsLoading(false);
      } catch (err) {
        console.log(err);
        setIsError(true);
        setIsLoading(false);
      }
    },
    []
  );

  const [
    distributeMessage,
    { isLoading: isDistributing, isSuccess: isDistributingStarted },
  ] = communicationMessagesApi.useDistributeMessageMutation();
  const { data: messageStatus, isLoading: isCheckingStatus } =
    communicationMessagesApi.useGetMessageStatusQuery(
      {
        coopId,
        messageId,
      },
      {
        pollingInterval: 1000,
        skip:
          !!!coopId || !!!messageId || !isDistributingStarted || isDistributed,
      }
    );

  const recipientsBody: RecipientsBody = useMemo(
    () => ({
      communicationMessageId: communicationMessage?.Id ?? null,
      coopId: communicationMessage?.Cooperative?.Id ?? null,
      distributingList: null,
      selectedRoles: communicationMessage?.RecipientRoles ?? [],
      selectedSpaces: communicationMessage?.Spaces ?? [],
      sendEmail: communicationMessage?.SendEmail ?? false,
      sendFloorApp: communicationMessage?.SendFloorApp ?? false,
      sendPortalMessage: communicationMessage?.SendPortalMessage ?? false,
      sendSms: communicationMessage?.SendSms ?? false,
    }),
    [communicationMessage]
  );

  const { recipients } = communicationMessagesApi.useGetRecipientsListQuery(
    {
      recipientsBody,
    },
    {
      skip: !!!recipientsBody.coopId,
      selectFromResult: ({ data }) => ({
        recipients: data,
      }),
    }
  );

  const handleDistributeMessage = useCallback(() => {
    if (coopId && messageId) {
      distributeMessage({ coopId, messageId });
    }
  }, [coopId, messageId, distributeMessage]);

  const handleRefreshMassMessage = useCallback(() => {
    if (coopId && messageId) {
      getInitialData(coopId, messageId);
    }
  }, [getInitialData, coopId, messageId]);

  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessDialog(false);
    handleRefreshMassMessage();
  }, [handleRefreshMassMessage]);

  useEffect(() => {
    if (coopId && messageId) {
      getInitialData(coopId, messageId);
    }
  }, [coopId, messageId, getInitialData]);

  useEffect(() => {
    if (messageStatus) {
      setIsDistributed(
        messageStatus === MassMessageStatusCode.Distributed ||
          messageStatus === MassMessageStatusCode.PendingSend
      );
    }
  }, [messageStatus]);

  useEffect(() => {
    if (isDistributed) {
      setShowSuccessDialog(true);
    }
  }, [isDistributed]);

  useEffect(() => {
    return () => {
      communicationMessagesApi.util.resetApiState();
    };
  }, []);

  const hasRecipients = recipients ? !!recipients.length : false;

  return (
    <MassMessageDetails
      buildings={initialMassMessageData.buildings}
      communicationMessage={initialMassMessageData.communicationMessage}
      hasRecipients={hasRecipients}
      isError={isError}
      isLoading={isLoading}
      isUploading={isDistributing || isCheckingStatus}
      rolesEntities={initialMassMessageData.rolesEntities}
      showSuccessDialog={showSuccessDialog}
      onClose={handleClose}
      onCloseSuccessModal={handleCloseSuccessModal}
      onDistribute={handleDistributeMessage}
      onRefresh={handleRefreshMassMessage}
    />
  );
};

export default MassMessage;
